import { useMemo, useState } from "react";
import { useAppSelector } from "@/app/hooks";
import {
  addOnOnlyCount,
  CATEGORIES,
  Category,
  categoryIntros,
  cumulativeIncludedCounts,
  newAtStageCounts,
  Offering,
  offeringCatalog,
  StageCounts,
  StageName,
  STAGES,
} from "../config/offeringCatalog";

export type GroupBy = "category" | "stage";
export type CategoryFilter = Category | "All";

export interface OfferingGroup {
  key: string;
  label: string;
  sub: string;
  intro: string | null;
  offerings: Offering[];
}

interface UseStageFeatureMatrixResult {
  groupBy: GroupBy;
  setGroupBy: (groupBy: GroupBy) => void;
  category: CategoryFilter;
  setCategory: (category: CategoryFilter) => void;
  groups: OfferingGroup[];
  visibleCount: number;
  totalCount: number;
  /** Header counts always describe the full catalog, not the filter. */
  includedCounts: StageCounts;
  newCounts: StageCounts;
  addOnOnly: number;
  /** The company's stage when it is one of the matrix columns, else null. */
  currentStage: StageName | null;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

function groupByCategory(offerings: Offering[]): OfferingGroup[] {
  return CATEGORIES.map((category) => {
    const rows = offerings.filter((o) => o.category === category);
    return {
      key: category,
      label: category,
      sub: plural(rows.length, "offering"),
      intro: categoryIntros[category] ?? null,
      offerings: rows,
    };
  }).filter((group) => group.offerings.length > 0);
}

function groupByStage(offerings: Offering[]): OfferingGroup[] {
  const stageGroups: OfferingGroup[] = STAGES.map((stage) => {
    const rows = offerings.filter((o) => o.includedFrom === stage);
    return {
      key: stage,
      label: `Included from ${stage}`,
      sub: `${rows.length} new at this stage`,
      intro: null,
      offerings: rows,
    };
  });
  const addOnRows = offerings.filter((o) => o.includedFrom === null);
  stageGroups.push({
    key: "add-on-only",
    label: "Add-on only",
    sub: plural(addOnRows.length, "offering"),
    intro: null,
    offerings: addOnRows,
  });
  return stageGroups.filter((group) => group.offerings.length > 0);
}

/**
 * Supplies the static catalog grouped and filtered for display, plus the
 * company's current stage from `userAppSettings.companyStage`.
 *
 * The current stage is matched by NAME: DB stage positions shift when the
 * ladder changes (Pre-EP moved Build from 1 to 2), and stages without a
 * matrix column (Pre-EP) simply get no marker.
 */
export function useStageFeatureMatrix(): UseStageFeatureMatrixResult {
  const [groupBy, setGroupBy] = useState<GroupBy>("category");
  const [category, setCategory] = useState<CategoryFilter>("All");

  const stageName = useAppSelector(
    (state) =>
      state.userAppSettings.userAppSettings?.companyStage?.name ?? null,
  );
  const currentStage =
    stageName !== null && (STAGES as readonly string[]).includes(stageName)
      ? (stageName as StageName)
      : null;

  const visible = useMemo(
    () =>
      category === "All"
        ? offeringCatalog
        : offeringCatalog.filter((o) => o.category === category),
    [category],
  );

  const groups = useMemo(
    () =>
      groupBy === "category" ? groupByCategory(visible) : groupByStage(visible),
    [groupBy, visible],
  );

  return {
    groupBy,
    setGroupBy,
    category,
    setCategory,
    groups,
    visibleCount: visible.length,
    totalCount: offeringCatalog.length,
    includedCounts: cumulativeIncludedCounts(offeringCatalog),
    newCounts: newAtStageCounts(offeringCatalog),
    addOnOnly: addOnOnlyCount(offeringCatalog),
    currentStage,
  };
}
